import { useNavigate } from 'react-router-dom';
import TemplatesPage from '../../TemplatesPage';
import { useLegacyAppSession } from '../../contexts/LegacyAppSessionContext';
import { addTemplate } from '../../hooks/useTemplates';
import { showToastError, showToastSuccess } from '../../components/ToastNotifications';
import { throwIfLegacyResultError } from '../../utils/requestErrors';
import { getCrossFormatCloneName, parseTemplateForClone } from '../../utils/templateClone';

export default function TemplatesContainer() {
  const navigate = useNavigate();
  const { org, currentUser } = useLegacyAppSession();

  return (
    <TemplatesPage
      organizationId={org?.id ?? null}
      currentUser={currentUser}
      onAdd={(formatSelection) => {
        if (!formatSelection?.format && !formatSelection?.dpi) {
          navigate('/templates/new');
          return;
        }
        navigate('/templates/new', {
          state: { format: formatSelection?.format, dpi: formatSelection?.dpi },
        });
      }}
      onAddFromTemplate={(template, cloneName, formatSelection, marketIds, provenance) => navigate('/templates/new', {
        state: {
          sourceTemplate: template,
          templateName: cloneName,
          format: formatSelection?.format,
          dpi: formatSelection?.dpi,
          marketIds,
          provenance,
        },
      })}
      onEdit={(template) => navigate(`/templates/${template.id}/design`, { state: { template } })}
      onCloneAs={(template, target) => {
        const sourceTemplate = parseTemplateForClone(template.content, {
          outputFormat: template.outputFormat,
          dpi: template.dpi,
        });
        if (!sourceTemplate) {
          showToastError('The template could not be cloned because its design data is invalid.');
          return;
        }
        const sourceBrandIds = template.brandIds ?? template.BrandIds ?? sourceTemplate.brandIds;
        navigate('/templates/new', {
          state: {
            sourceTemplate,
            templateName: getCrossFormatCloneName(template.name, target),
            format: target.format,
            dpi: target.dpi,
            marketIds: template.marketIds || template.MarketIds || [],
            ...(sourceBrandIds === undefined ? {} : { brandIds: sourceBrandIds }),
          },
        });
      }}
      onCopy={async (template, allNames: string[]) => {
        if (!org?.id) return false;
        const nameSet = new Set(allNames);
        const baseName = `${template.name} - Clone`;
        let cloneName = baseName;

        if (nameSet.has(cloneName)) {
          let index = 2;
          while (nameSet.has(`${baseName} ${index}`)) {
            index++;
          }
          cloneName = `${baseName} ${index}`;
        }

        try {
          const result = await addTemplate({
            organizationId: org.id,
            name: cloneName,
            content: template.content,
            marketIds: template.marketIds || [],
          });
          throwIfLegacyResultError(result, 'The template could not be copied. Please try again.');
          showToastSuccess(`${cloneName} successfully created and added below.`);
          return true;
        } catch (error) {
          console.error('Failed to clone template:', error);
          showToastError('The template could not be copied. Please try again.');
          return false;
        }
      }}
    />
  );
}
